/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import {
  QueryContainer,
  OutlinedButton,
  ContainedButton,
  AddDeptModal,
  SuccessModal,
} from '../index';
import { ViewAllProjects } from './ViewAllProjects';
import { ViewAllDepartments } from './ViewAllDepartments';
import { Grid } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { apiUrl } from '../../utilities/apiUrl';
import { AddProjModal } from '../Modal/AddProjModal';

const bigWidth = { width: 250 };
const medWidth = { width: 180 };

export const General = (props) => {
  const employeeList = props.employeeList;
  const [departmentList, setDepartmentList] = useState([]);
  const [isProject, setIsProject] = useState(false);
  const [projectData, setProjectData] = useState([]);
  const projEntryPerPage = 6;
  const [projSlice, setProjSlice] = useState([0, projEntryPerPage]);
  const [addDeptModalOpen, setAddDeptModalOpen] = useState(false);
  const [addProjModalOpen, setAddProjModalOpen] = useState(false);
  const [employeeName, setEmployeeName] = useState([]);
  const [projectName, setProjectName] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  // Dept modal states
  const [deptName, setDeptName] = useState('');
  const [deptDesc, setDeptDesc] = useState('');
  const [deptMandaWorkDesc, setDeptMandaWorkDesc] = useState('');
  const [deptLocation, setDeptLocation] = useState('');
  const [deptMessaging, setDeptMessaging] = useState('');
  const [addDeptSuccessOpen, setAddDeptSuccessOpen] = useState(false);
  // Proj modal states
  const [projName, setProjName] = useState('');
  const [projDesc, setProjDesc] = useState('');
  const [projHoursWorked, setProjHoursWorked] = useState(0);
  const [projStatus, setProjStatus] = useState(true);
  const [addProjSuccessOpen, setAddProjSuccessOpen] = useState(false);

  let deptMembers = [];

  // GET ALL DEPARTMENT DATA
  useEffect(() => {
    fetch(apiUrl('/department'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setDepartmentList(
          data.data.map((dept) => ({
            ...dept,
            members: props.employeeList.filter(
              (emp) => emp.deptid === dept._id
            ),
          }))
        );
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GET ALL PROJECT DATA
  useEffect(() => {
    fetch(apiUrl('/project'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setProjectData(data.data);
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PAGE CHANGE HANDLER
  const handlePageChange = (e, page) => {
    let currPage = page - 1;
    page === 1
      ? setProjSlice([0, projEntryPerPage])
      : setProjSlice([currPage * projEntryPerPage, page * projEntryPerPage]);
  };

  // SELECTING MULTIPLE DEPARTMENT EMPLOYEES
  const handleSelectMembersChange = (event) => {
    const {
      target: { value },
    } = event;

    const member = typeof value === 'string' ? value.split(',') : value;
    setEmployeeName(member);
  };

  // SELECTING MULTIPLE PROJECTS
  const handleSelectProjectsChange = (event) => {
    const {
      target: { value },
    } = event;
    setProjectName(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value
    );
  };

  // ADD NEW DEPARTMENT HANDLER
  const handleAddDepartment = () => {
    // GET ALL EMPIDS OF THE SELECTED MEMBERS
    employeeName.forEach((member) => {
      employees.forEach((emp) => {
        if (member === emp.name) {
          deptMembers.push(emp.id);
        }
      });
    });
    fetch(apiUrl('/department'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        deptname: deptName,
        location: deptLocation,
        deptdesc: deptDesc,
        mandaworkdesc: deptMandaWorkDesc,
        messaging: deptMessaging,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          fetch(apiUrl('/department'), {
            method: 'GET',
            credentials: 'include',
          })
            .then((response) => response.json())
            .then((data) => {
              // get id of the newly added department
              const newDeptId = data.data[data.data.length - 1]._id;

              // add the members
              handleAddMembers(newDeptId);
            });
        } else {
          alert(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // ADD NEW PROJECT HANDLER
  const handleAddProject = () => {
    fetch(apiUrl('/project'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        deptid: '',
        projecttitle: projName,
        projectdesc: projDesc,
        hoursworked: projHoursWorked,
        active: projStatus,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setAddProjModalOpen(false);
          setAddProjSuccessOpen(true);
          resetProjFields();
        } else {
          alert(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleAddMembers = (newDeptId) => {
    fetch(apiUrl('/employee'), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        empids: deptMembers,
        newData: { deptid: newDeptId },
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setAddDeptModalOpen(false);
          setAddDeptSuccessOpen(true);
          resetDeptFields();
        } else {
          alert(data.message);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const resetDeptFields = () => {
    setDeptName('');
    setDeptDesc('');
    setDeptMandaWorkDesc('');
    setDeptLocation('');
    deptMembers = [];
    setEmployeeName([]);
    setDeptMessaging('');
  };

  const resetProjFields = () => {
    setProjName('');
    setProjDesc('');
    setProjHoursWorked('');
    setProjStatus();
  };

  const getEmployeeNames = () => {
    const membersList = employeeList.map((emp) => ({
      id: emp._id,
      name: `${emp.firstname} ${emp.lastname}`,
    }));

    setEmployees(membersList);
  };

  return (
    <>
      <QueryContainer>
        <Grid item sx={{ display: 'flex' }}>
          <Grid item>
            <OutlinedButton
              onClick={() => {
                setIsProject(false);
                localStorage.setItem('isProject', false);
              }}
              btnTitle="View All Departments"
              style={{
                ...bigWidth,
                bgcolor: !isProject ? '#84D8C5' : 'none',
                color: !isProject ? '#FFF' : '#84D8C5',
                '&:hover': {
                  color: '#FFF',
                  borderColor: '#84d8c5',
                  bgcolor: !isProject
                    ? '#84d8c5'
                    : 'rgb(132, 216, 197, 0.2) !important',
                },
              }}
            />
          </Grid>
          <Grid item sx={{ ml: 3 }}>
            <OutlinedButton
              onClick={() => {
                setIsProject(true);
                localStorage.setItem('isProject', true);
              }}
              btnTitle="View All Projects"
              style={{
                ...bigWidth,
                bgcolor: isProject ? '#84D8C5' : 'none',
                color: isProject ? '#FFF' : '#84D8C5',
                '&:hover': {
                  color: '#FFF',
                  borderColor: '#84d8c5',
                  bgcolor: isProject
                    ? '#84d8c5'
                    : 'rgb(132, 216, 197, 0.2) !important',
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid item sx={{ display: 'flex' }}>
          <Grid item>
            <ContainedButton
              onClick={() => {
                setAddDeptModalOpen(true);
                getEmployeeNames();
                projectData.forEach((project) => {
                  setProjects((prevProject) => [
                    ...prevProject,
                    project.projecttitle,
                  ]);
                });
              }}
              btnTitle="Add Department"
              startIcon={<AddIcon />}
              style={medWidth}
            />
          </Grid>
          <Grid item sx={{ ml: 3 }}>
            <ContainedButton
              onClick={() => {
                setAddProjModalOpen(true);
              }}
              btnTitle="Add New Project"
              startIcon={<AddIcon />}
              style={medWidth}
            />
          </Grid>
        </Grid>
      </QueryContainer>
      {isProject ? (
        <ViewAllProjects
          length={projectData.length}
          slice={projSlice}
          entryPerPage={projEntryPerPage}
          pageCount={Math.floor(projectData.length / projEntryPerPage)}
          handlePageChange={handlePageChange}
          projectData={projectData}
        />
      ) : (
        <ViewAllDepartments
          departmentList={departmentList}
          employeeList={employeeList}
        />
      )}
      <AddDeptModal
        open={addDeptModalOpen}
        onClose={() => setAddDeptModalOpen(false)}
        names={employees}
        projects={projects}
        employeeName={employeeName}
        projectName={projectName}
        handleSelectMembersChange={handleSelectMembersChange}
        handleSelectProjectsChange={handleSelectProjectsChange}
        primaryClickHandler={handleAddDepartment}
        secondaryClickHandler={() => setAddDeptModalOpen(false)}
        deptNameOnChange={(e) => setDeptName(e.target.value)}
        deptDescOnChange={(e) => setDeptDesc(e.target.value)}
        deptMandaWorkDescOnChange={(e) => setDeptMandaWorkDesc(e.target.value)}
        deptLocationOnChange={(e) => setDeptLocation(e.target.value)}
        deptMessagingOnChange={(e) => setDeptMessaging(e.target.value)}
      ></AddDeptModal>

      <AddProjModal
        open={addProjModalOpen}
        onClose={() => setAddProjModalOpen(false)}
        primaryClickHandler={handleAddProject}
        secondaryClickHandler={() => setAddProjModalOpen(false)}
        projNameOnChange={(e) => setProjName(e.target.value)}
        projDescOnChange={(e) => setProjDesc(e.target.value)}
        projHoursWorkedOnChange={(e) => setProjHoursWorked(e.target.value)}
        projStatusOnChange={(e) => setProjStatus(e.target.value)}
      ></AddProjModal>

      <SuccessModal
        open={addDeptSuccessOpen}
        onClose={() => {
          setAddDeptSuccessOpen(false);
          window.location.reload();
        }}
        close={() => {
          setAddDeptSuccessOpen(false);
          window.location.reload();
        }}
        desc={`You have successfully added ${deptName} Department.`}
      ></SuccessModal>

      <SuccessModal
        open={addProjSuccessOpen}
        onClose={() => {
          setAddProjSuccessOpen(false);
          window.location.reload();
        }}
        close={() => {
          setAddProjSuccessOpen(false);
          window.location.reload();
        }}
        desc={`You have successfully added ${projName} Project.`}
      ></SuccessModal>
    </>
  );
};
